import Big from "big.js"
import { concat, groupBy, last, orderBy, uniq } from "lodash"

import { formatIsoDateTime } from "@axtesys/react-tools"

import {
  ArticleInput,
  DetailedReceiptItemFragment,
  DiscountInput,
  HistoricInvoiceItemInput,
  InvoiceItemInput,
  ReceiptInput,
  TipAttributionFragment,
  TipAttributionInput,
  TipTransferFragment,
  TipTransferInput,
  ValueTransferFlagsFragment,
  ValueTransferFragment,
  ValueTransferInput,
} from "~shared/api/graphql/types"
import { logWarn } from "~shared/feature/Logging/lib"
import { transformFromGraphQLToSharedReceiptItem } from "~shared/feature/Receipt/lib"
import { t_General } from "~shared/feature/Translation/lib"
import { maxOfBigs, minOfBigs, sumBigArray } from "~shared/lib/Big"
import { bigToEuros, eurosToBig } from "~shared/lib/Money"
import {
  ArticleGroup,
  ArticleGroupId,
  ArticleId,
  DetailedReceiptItem,
  Discount,
  InputReceipt,
  InvoiceItem,
  ItemArticle,
  MainGroup,
  MainGroupId,
  OutputInvoiceItem,
  OutputReceipt,
  PaymentMethod,
  PaymentMethodId,
  PreOutputReceipt,
  TaxInfo,
  TipAttribution,
  TipTransfer,
  TotalTaxInfo,
  ValueTransfer,
} from "~shared/types"

import { determineOutputType } from "../Printers/lib"
import {
  evaluateInvoiceItem,
  transformDiscountsToDiscountsWithType,
} from "../ShoppingCart/lib"

// Composition functions

export function createReceipt(receipt: InputReceipt): PreOutputReceipt {
  // The type in which a receipt is passed
  // to the customer (email, paper [print]).
  const receivedAs = determineOutputType(receipt.printerId)

  // Order `itemsHistory` by its `modifiedAt` timestamp
  // to make sure that we send it correctly to the backend.
  const itemsHistory = orderBy(
    receipt.itemsHistory,
    historyEntry => historyEntry.modifiedAt,
  )

  // Compute items that should be marked as zero/voided.
  const zeroAndVoidedItems = retrieveZeroAndVoidedItems(
    receipt.items,
    itemsHistory,
  )

  // Order `cartDiscountsHistory` by its `createdAt` timestamp
  // to make sure that we send it correctly to the backend.
  const cartDiscountsHistory = orderBy(
    receipt.cartDiscountsHistory,
    historyEntry => historyEntry.createdAt,
  )

  // Omit empty `payments` (they should not be on the receipt).
  // This is needed in case of multi payment method receipts.
  // The initial payment method could be set to zero.
  const payments = receipt.payments.filter(payment => !payment.amount.eq(0))

  if (receipt.payments.length != payments.length) {
    logWarn("Empty payment constraint: Zero amount filter applied", {
      originalPayments: receipt.payments,
      filteredPayments: payments,
    })
  }

  // Calculate the summarized `taxInfos` and `totalTaxInfo` of the receipt.
  // `taxInfos`: Sums grouped by their corresponding `taxRate`.
  // `totalTaxInfo`: Total sums of all `taxRate` groups / invoice items.
  const { taxInfos, totalTaxInfo } = calculateTaxInfos(
    receipt.items,
    receipt.roundingDifference,
  )

  return {
    ...receipt,
    receivedAs,
    itemsHistory,
    zeroAndVoidedItems,
    cartDiscountsHistory,
    payments,
    taxInfos,
    totalTaxInfo,
    signatureDeviceFailed: false,
  }
}

function retrieveZeroAndVoidedItems(
  finalItems: OutputInvoiceItem[],
  itemsHistory: InvoiceItem[],
) {
  const itemsToZero = retrieveZeroAmountItems(finalItems)
  const itemsToVoid = retrieveVoidedItems(finalItems, itemsHistory)
  return concat(itemsToZero, itemsToVoid)
}

function retrieveZeroAmountItems(
  items: OutputInvoiceItem[],
): OutputInvoiceItem[] {
  return items.filter(item => item.amount.eq(0))
}

function retrieveVoidedItems(
  finalItems: OutputInvoiceItem[],
  itemsHistory: InvoiceItem[],
): OutputInvoiceItem[] {
  const voidedItems: OutputInvoiceItem[] = []

  // Find the unique item IDs from the history.
  const invoiceItemIds = uniq(
    itemsHistory.map(invoiceItem => invoiceItem.invoiceItemId),
  )

  // For each ID, check the difference between max and final amount.
  for (const invoiceItemId of invoiceItemIds) {
    // At this point there must be a history for this specific item.
    const itemHistory = itemsHistory.filter(
      invoiceItem => invoiceItem.invoiceItemId == invoiceItemId,
    )

    // Therefore, there must be at least one configuration present.
    const lastItemConfiguration = last(itemHistory)!

    // Extract the amount history.
    const itemAmountHistory = itemHistory.map(item => item.amount)

    // Dependent on the last item configuration
    // and its `isNegativeAmount` property,
    // either retrieve the min OR max amount
    // of the `itemHistory` for the void evaluation.
    const comparisonAmount =
      (lastItemConfiguration.isNegativeAmount
        ? minOfBigs(itemAmountHistory)
        : maxOfBigs(itemAmountHistory)) ?? Big(0)

    // It is not clear that there is always a final representation
    // of the invoice item since empty / zero items
    // are stripped from the final item list.
    const finalState = finalItems.find(
      invoiceItem => invoiceItem.invoiceItemId == invoiceItemId,
    )

    // In case there is no `finalState` with its amount present,
    // the item has been zeroed in the cart.
    const finalAmount = finalState?.amount ?? Big(0)

    // The difference is the amount of voided items.
    const voidAmount = comparisonAmount.sub(finalAmount)

    // Do not push/add the current item to the list
    // in case the `voidedAmount` equals zero,
    // because it does not make sense.
    if (voidAmount.eq(0)) continue

    // Transform all item discounts from the last configuration.
    // We solely want to include item discounts here
    // because we only want to stress position voids.
    // As the cart discount is visually not applied in the shopping cart,
    // voids are seemingly made on an invoice item basis.
    const discounts = transformDiscountsToDiscountsWithType(
      lastItemConfiguration.discounts,
      "ItemDiscount",
    )

    // Use the last configuration,
    // overwrite the amount and re-evaluate
    // to calculate the correct tax info.
    voidedItems.push(
      evaluateInvoiceItem({
        ...lastItemConfiguration,
        discounts,
        amount: voidAmount,
      }),
    )
  }

  return voidedItems
}

function calculateTaxInfos(
  items: OutputInvoiceItem[],
  roundingDifference?: Big,
) {
  const groupedTaxInfos = groupBy(
    items.map(item => item.taxInfo),
    taxInfo => taxInfo.taxRate,
  )

  const taxInfos: TaxInfo[] = orderBy(
    Object.entries(groupedTaxInfos).map(([taxRate, infos]) => ({
      ...calculateSharedTaxInfoSums(infos),
      taxRate: +taxRate,
    })),
    taxInfo => taxInfo.taxRate,
  )

  const preTotalTaxInfo = calculateSharedTaxInfoSums(taxInfos)
  const totalTaxInfo: TotalTaxInfo = {
    ...preTotalTaxInfo,
    correctedGross: preTotalTaxInfo.gross.minus(roundingDifference ?? Big(0)),
  }

  return { taxInfos, totalTaxInfo }
}

function calculateSharedTaxInfoSums(
  taxInfos: TaxInfo[],
): Omit<TotalTaxInfo, "correctedGross"> {
  const net = sumBigArray(taxInfos.map(info => info.net))
  const tax = sumBigArray(taxInfos.map(info => info.tax))
  const gross = sumBigArray(taxInfos.map(info => info.gross))
  const discount = sumBigArray(taxInfos.map(info => info.discount))
  const undiscountedGross = sumBigArray(
    taxInfos.map(info => info.undiscountedGross),
  )

  return { net, tax, gross, discount, undiscountedGross }
}

// Frontend to GraphQL transformation functions

export function transformOutputReceiptToGraphQL(
  receipt: OutputReceipt,
  mainGroups: Record<MainGroupId, MainGroup>,
  articleGroups: Record<ArticleGroupId, ArticleGroup>,
): ReceiptInput {
  return {
    receiptId: receipt.receiptId,

    company: receipt.company,
    cashRegister: receipt.cashRegister,
    operator: receipt.operator,

    createdAt: receipt.createdAt,
    receiptNumber: receipt.receiptNumber,
    receivedAs: receipt.receivedAs,

    itemsHistory: transformInvoiceItemsHistoryToGraphQL(
      receipt.itemsHistory,
      mainGroups,
      articleGroups,
    ),
    items: transformOutputInvoiceItemsToGraphQL(
      receipt.items,
      mainGroups,
      articleGroups,
    ),
    zeroAndVoidedItems: transformOutputInvoiceItemsToGraphQL(
      receipt.zeroAndVoidedItems,
      mainGroups,
      articleGroups,
    ),

    payments: receipt.payments.map(transformValueTransferToGraphQL),
    taxInfos: receipt.taxInfos.map(taxInfo => ({
      ...transformTaxInfoToGraphQL(taxInfo),
      taxRate: taxInfo.taxRate,
    })),
    totalTaxInfo: {
      ...transformTaxInfoToGraphQL(receipt.totalTaxInfo),
      correctedGross: bigToEuros(receipt.totalTaxInfo.correctedGross),
    },

    billingAddress: receipt.billingAddress,
    department: receipt.department,
    shippingAddress: receipt.shippingAddress,
    receiptText: receipt.receiptText,

    cartDiscountsHistory: receipt.cartDiscountsHistory.map(historyEntry => ({
      createdAt: formatIsoDateTime(historyEntry.createdAt),
      discounts: historyEntry.discounts.map(transformDiscountToGraphQL),
    })),
    tips: receipt.tips?.map(transformTipTransferToGraphQL),
    payouts: receipt.payouts?.map(transformValueTransferToGraphQL),

    hobexReceiptData: receipt.hobexResponse,
    gpTomReceiptData: receipt.gpTomResponse,
  }
}

function transformInvoiceItemsHistoryToGraphQL(
  items: InvoiceItem[],
  mainGroups: Record<MainGroupId, MainGroup>,
  articleGroups: Record<ArticleGroupId, ArticleGroup>,
): HistoricInvoiceItemInput[] {
  return items.map(item => ({
    invoiceItemId: item.invoiceItemId,
    modifiedAt: item.modifiedAt.toISOString(),

    amount: item.amount.toNumber(),
    article: transformInvoiceItemArticleToGraphQL(
      item.article,
      mainGroups,
      articleGroups,
    ),
    discounts: item.discounts.map(transformDiscountToGraphQL),

    additionalText: item.additionalText,
  }))
}

function transformOutputInvoiceItemsToGraphQL(
  items: OutputInvoiceItem[],
  mainGroups: Record<MainGroupId, MainGroup>,
  articleGroups: Record<ArticleGroupId, ArticleGroup>,
): InvoiceItemInput[] {
  return items.map(item => ({
    invoiceItemId: item.invoiceItemId,
    modifiedAt: item.modifiedAt.toISOString(),

    amount: item.amount.toNumber(),
    article: transformInvoiceItemArticleToGraphQL(
      item.article,
      mainGroups,
      articleGroups,
    ),
    discounts: item.discounts.map(discount => ({
      name: discount.name,
      initialType: discount.initialType,
      discountType: discount.discountType,
      percent: discount.percent?.toNumber(),
      absolute: bigToEuros(discount.absolute),
    })),
    isDiscountBlocked: item.isDiscountBlocked,
    discountedPricePerUnit: bigToEuros(
      item.discountedPricePerUnit ?? item.article.price,
    ),
    taxInfo: transformTaxInfoToGraphQL(item.taxInfo),

    attachedToInvoiceItemId: item.attachedToInvoiceItemId,
    createdAt: item.createdAt.toISOString(),
    additionalText: item.additionalText,
    isStandalone: item.isStandalone,
  }))
}

function transformInvoiceItemArticleToGraphQL(
  article: ItemArticle,
  mainGroups: Record<MainGroupId, MainGroup>,
  articleGroups: Record<ArticleGroupId, ArticleGroup>,
): ArticleInput {
  return {
    articleId: article.articleId,
    mainGroupId: article.mainGroupId,
    articleGroupId: article.articleGroupId,

    name: article.name,
    taxRate: article.taxRate,
    price: bigToEuros(article.price),

    mainGroupName:
      mainGroups[article.mainGroupId]?.name ?? t_General().tGeneral("unknown"),
    articleGroupName:
      articleGroups[article.articleGroupId]?.name ??
      t_General().tGeneral("unknown"),

    depositArticleId: article.depositArticleId,
    number: article.number,
  }
}

function transformDiscountToGraphQL(discount: Discount): DiscountInput {
  return {
    name: discount.name,
    absolute: bigToEuros(discount.absolute),
    percent: discount.percent?.toNumber(),
  }
}

function transformValueTransferToGraphQL(
  transfer: ValueTransfer,
): ValueTransferInput {
  return {
    paymentMethodId: transfer.paymentMethodId,
    paymentMethodName: transfer.methodName,
    moneyAmount: bigToEuros(transfer.amount),
    flags: transfer.flags,
  }
}

function transformTipAttributionToGraphQL(
  transfer: TipAttribution,
): TipAttributionInput {
  return {
    paymentMethodId: transfer.paymentMethodId,
    paymentMethodName: transfer.methodName,
    flags: transfer.flags,
  }
}

function transformTipTransferToGraphQL(
  transfer: TipTransfer,
): TipTransferInput {
  return {
    moneyAmount: bigToEuros(transfer.amount),
    originateFrom: transformTipAttributionToGraphQL(transfer.originateFrom),
    considerFor: transfer.considerFor
      ? transformTipAttributionToGraphQL(transfer.considerFor)
      : undefined,
  }
}

function transformTaxInfoToGraphQL(taxInfo: Omit<TaxInfo, "taxRate">) {
  return {
    net: bigToEuros(taxInfo.net),
    tax: bigToEuros(taxInfo.tax),
    gross: bigToEuros(taxInfo.gross),
    discount: bigToEuros(taxInfo.discount),
    undiscountedGross: bigToEuros(taxInfo.undiscountedGross),
  }
}

// GraphQL to frontend transformation functions

export function transformFromGraphQLToItemArticle({
  initialArticleBps,
}: DetailedReceiptItemFragment): ItemArticle {
  return {
    articleId: initialArticleBps.articleId,
    mainGroupId: initialArticleBps.mainGroupId,
    articleGroupId: initialArticleBps.articleGroupId,
    name: initialArticleBps.name,
    taxRate: initialArticleBps.taxRate,
    price: eurosToBig(initialArticleBps.price),
    depositArticleId: initialArticleBps.depositArticleId,
    number: initialArticleBps.number,
  }
}

export function transformFromGraphQLToDetailedReceiptItem(
  item: DetailedReceiptItemFragment,
  attachedItemArticleRecord: Record<ArticleId, ItemArticle>,
): DetailedReceiptItem {
  return {
    ...transformFromGraphQLToSharedReceiptItem(item),
    article: transformFromGraphQLToItemArticle(item),

    // This property will be overwritten with a new matching ID
    // in case the respective item is attached to a main invoice item
    // and the receipt is inverted
    // (currently by either carrying out a refund or cancel operation).
    attachedToInvoiceItemId: item.attachedToInvoiceItemId,

    // Relink a potential article from an attached item back to its main one.
    attachedArticle: attachedItemArticleRecord[item.id],

    isStandalone: item.isStandalone,
  }
}

function retrieveFlagsForTransfer(
  paymentMethods: Record<PaymentMethodId, PaymentMethod>,
  paymentMethodId: PaymentMethodId,
  inputFlags?: ValueTransferFlagsFragment,
) {
  if (inputFlags) return inputFlags

  const paymentMethod = paymentMethods?.[paymentMethodId]

  if (!paymentMethod)
    throw Error("retrieveFlagsForTransfer - Payment method missing")

  return {
    ...paymentMethod.properties,
    ...paymentMethod.programmaticProperties,
  }
}

export function transformFromGraphQLToValueTransfers(
  valueTransfers: ValueTransferFragment[],
  paymentMethods: Record<PaymentMethodId, PaymentMethod>,
): ValueTransfer[] {
  return valueTransfers.map(transfer => ({
    paymentMethodId: transfer.paymentMethodId,
    methodName: transfer.paymentMethodName,
    amount: eurosToBig(transfer.moneyAmount),
    flags: retrieveFlagsForTransfer(
      paymentMethods,
      transfer.paymentMethodId,
      transfer.flags,
    ),
  }))
}

function transformFromGraphQLToTipAttribution(
  tipAttribution: TipAttributionFragment,
  paymentMethods: Record<PaymentMethodId, PaymentMethod>,
): TipAttribution {
  return {
    paymentMethodId: tipAttribution.paymentMethodId,
    methodName: tipAttribution.paymentMethodName,
    flags: retrieveFlagsForTransfer(
      paymentMethods,
      tipAttribution.paymentMethodId,
      tipAttribution.flags,
    ),
  }
}

export function transformFromGraphQLToTipTransfers(
  tipTransfers: TipTransferFragment[],
  paymentMethods: Record<PaymentMethodId, PaymentMethod>,
): TipTransfer[] {
  return tipTransfers.map(transfer => ({
    amount: eurosToBig(transfer.moneyAmount),
    originateFrom: transformFromGraphQLToTipAttribution(
      transfer.originateFrom,
      paymentMethods,
    ),
    considerFor: transfer.considerFor
      ? transformFromGraphQLToTipAttribution(
          transfer.considerFor,
          paymentMethods,
        )
      : undefined,
  }))
}
